<template>
    <modal-form
        editTitle="Portal-Feld bearbeiten"
        createTitle="Neues Portal-Feld hinzufügen"
    >
        <table>
            <tr>
                <th class="is-top"><label class="label">Ebene:</label></th>
                <td>
                    <div class="control">
                        <multiselect
                            v-model="entityType"
                            :options="entityTypeOptions"
                            label="value"
                            track-by="key"
                            class="is-shorter"
                            :allow-empty="false"
                            :disabled="editMode"
                        />
                    </div>
                </td>
            </tr>
            <tr v-if="form.entityType === 'passenger'">
                <th class="is-top"><label class="label">Feldart:</label></th>
                <td>
                    <div class="control">
                        <multiselect
                            v-model="fieldKind"
                            :options="fieldKindOptions"
                            label="value"
                            track-by="key"
                            class="is-shorter"
                            :allow-empty="false"
                            :disabled="editMode"
                        />
                    </div>
                    <p class="help">Standardfeld = vorhandenes Teilnehmer-Feld (Vorname, Nachname …). Eigenes Feld = zusätzliches Custom-Feld.</p>
                </td>
            </tr>
            <tr v-if="form.source === 'entity'">
                <th class="is-top"><label class="label">Standardfeld:</label></th>
                <td>
                    <div class="control">
                        <multiselect
                            v-model="standardField"
                            :options="entityFieldOptions"
                            label="label"
                            track-by="key"
                            class="is-shorter"
                            :allow-empty="false"
                            placeholder="Feld wählen"
                        />
                    </div>
                </td>
            </tr>
            <tr v-if="form.source !== 'entity'">
                <th class="is-top"><label for="fieldKey" class="label">Key:</label></th>
                <td>
                    <div class="control">
                        <!-- Key is the JSON map key — changing it on edit would orphan existing values -->
                        <input
                            type="text"
                            id="fieldKey"
                            class="input"
                            v-model="form.fieldKey"
                            :disabled="editMode"
                            placeholder="z.B. tShirtSize"
                        >
                    </div>
                    <p class="help">Nur Buchstaben/Ziffern/Unterstriche, beginnt mit Buchstabe. Nach dem Anlegen nicht mehr änderbar.</p>
                </td>
            </tr>
            <tr>
                <th class="is-top"><label for="label" class="label">Label:</label></th>
                <td>
                    <div class="control">
                        <input type="text" id="label" class="input" v-model="form.label">
                    </div>
                </td>
            </tr>
            <tr v-if="form.source !== 'entity'">
                <th class="is-top"><label class="label">Typ:</label></th>
                <td>
                    <div class="control">
                        <multiselect
                            v-model="type"
                            :options="typeOptions"
                            label="value"
                            track-by="key"
                            class="is-shorter"
                            :allow-empty="false"
                        />
                    </div>
                </td>
            </tr>
            <tr v-if="['select', 'checkboxes'].includes(form.type) && form.source !== 'entity'">
                <th class="is-top"><label class="label">Auswahloptionen:</label></th>
                <td>
                    <div class="control">
                        <multiselect
                            v-model="form.options"
                            :options="form.options || []"
                            :multiple="true"
                            :taggable="true"
                            tag-placeholder="Option hinzufügen"
                            placeholder="Option eintippen und Enter"
                            @tag="addOption"
                        />
                    </div>
                </td>
            </tr>
            <tr v-if="form.source !== 'entity'">
                <th class="is-top"><label class="label">Quelle:</label></th>
                <td>
                    <div class="control">
                        <multiselect
                            v-model="source"
                            :options="sourceOptions"
                            label="value"
                            track-by="key"
                            class="is-shorter"
                            :allow-empty="false"
                        />
                    </div>
                </td>
            </tr>
            <tr v-if="form.source === 'computed'">
                <th class="is-top"><label for="resolverKey" class="label">Resolver-Key:</label></th>
                <td>
                    <div class="control">
                        <input
                            type="text"
                            id="resolverKey"
                            class="input"
                            v-model="form.resolverKey"
                            placeholder="z.B. roomStats.fix_total"
                        >
                    </div>
                    <p class="help">Muss zu einem PortalFieldResolver im Code passen — ohne Resolver bleibt das Feld leer.</p>
                </td>
            </tr>
            <tr>
                <th class="is-top"><label for="sortOrder" class="label">Reihenfolge:</label></th>
                <td>
                    <div class="control">
                        <input type="number" id="sortOrder" class="input" v-model.number="form.sortOrder">
                    </div>
                    <p class="help">Bestimmt die Spaltenposition (10er-Schritte; Teilnehmer-Felder werden mit den Standardfeldern verzahnt).</p>
                </td>
            </tr>
            <tr v-if="form.entityType === 'order'">
                <th class="is-top"><label for="width" class="label">Spaltenbreite (px):</label></th>
                <td>
                    <div class="control">
                        <input type="number" id="width" class="input" v-model.number="form.width">
                    </div>
                </td>
            </tr>
            <tr v-if="form.source === 'manual' || form.source === 'entity'">
                <th class="is-top"><label class="label">Im Portal editierbar:</label></th>
                <td>
                    <toggle-switch v-model="form.editable">
                        <option :value="true">Ja</option>
                        <option :value="false">Nein</option>
                    </toggle-switch>
                    <p class="help">Nein = der Kunde sieht den Wert nur; gepflegt wird er von uns.</p>
                </td>
            </tr>
            <tr>
                <th class="is-top"><label class="label">Aktiv:</label></th>
                <td>
                    <toggle-switch v-model="form.active">
                        <option :value="true">Ja</option>
                        <option :value="false">Nein</option>
                    </toggle-switch>
                </td>
            </tr>
        </table>
    </modal-form>
</template>

<script>
import TabForm from '../form';
import { ToggleSwitch } from '@components/form';
import { ENTITY_TYPE_OPTIONS, TYPE_OPTIONS, SOURCE_OPTIONS, FIELD_KIND_OPTIONS, ENTITY_PASSENGER_FIELDS } from './options';

export default {
    mixins: [TabForm],

    property: 'portalFields',

    form: {
        entityType: 'passenger',
        fieldKey: '',
        label: '',
        type: 'text',
        options: null,
        source: 'manual',
        resolverKey: null,
        sortOrder: 0,
        width: null,
        active: true,
        editable: true,
    },

    data: function () {
        return {
            entityTypeOptions: ENTITY_TYPE_OPTIONS,
            typeOptions: TYPE_OPTIONS,
            sourceOptions: SOURCE_OPTIONS,
            fieldKindOptions: FIELD_KIND_OPTIONS,
            entityFieldOptions: ENTITY_PASSENGER_FIELDS,
        };
    },

    computed: {
        entityType: {
            get() { return this.entityTypeOptions.find(item => item.key === this.form.entityType); },
            set(option) {
                this.form.entityType = option.key;
                // Standard (entity) fields only exist on the passenger level.
                if (option.key !== 'passenger' && this.form.source === 'entity') {
                    this.form.source = 'manual';
                    this.form.fieldKey = '';
                    this.form.type = 'text';
                }
            },
        },

        // Standard (entity) vs custom field — backed by source so it survives edit.
        fieldKind: {
            get() {
                const key = this.form.source === 'entity' ? 'standard' : 'custom';
                return this.fieldKindOptions.find(item => item.key === key);
            },
            set(option) {
                if (option.key === 'standard') {
                    this.form.source = 'entity';
                    this.form.resolverKey = null;
                } else if (this.form.source === 'entity') {
                    this.form.source = 'manual';
                    this.form.fieldKey = '';
                    this.form.type = 'text';
                    this.form.label = '';
                }
            },
        },

        // Picking a standard field fills the persisted key/type from the catalog.
        standardField: {
            get() { return this.entityFieldOptions.find(item => item.key === this.form.fieldKey) || null; },
            set(option) {
                this.form.fieldKey = option.key;
                this.form.type = option.type;
                this.form.source = 'entity';
                if (!this.form.label) {
                    this.form.label = option.label;
                }
            },
        },

        type: {
            get() { return this.typeOptions.find(item => item.key === this.form.type); },
            set(option) { this.form.type = option.key; },
        },

        source: {
            get() { return this.sourceOptions.find(item => item.key === this.form.source); },
            set(option) {
                this.form.source = option.key;
                if (option.key === 'manual') {
                    this.form.resolverKey = null;
                }
            },
        },
    },

    methods: {
        addOption(value) {
            this.form.options = [...(this.form.options || []), value];
        },

        // The PUT payload must not carry serializer artifacts; the readOnly getter
        // from the GET response is not writable — strip it before submit.
        handleSubmit() {
            delete this.form.readOnly;
            this.handleFormSubmit();
        },
    },

    components: {
        ToggleSwitch,
    },
};
</script>
